/*
  WCAG 2.1 requires a contrast ratio of at least 3:1 for graphics and user interface components.
  We will get closer to this recommended value later on. For the moment we decided to use a 2.5:1 contrast ratio.
 */

const THRESHOLD: number = 2.5;
const PRIMARYVARIANTCOLORADJUSTMENT: number = 25;
const PRIMARYCONTRASTCOLORADJUSTMENT: number = 80;
const DARKTEXTCOLOR: string = "#262626"; // neutral-800
const LIGHTTEXTCOLOR: string = "#fafafa"; // neutral-50

export function getTextColorAndPrimaryVariants(backgroundColor: string): {
  textColor: string;
  primaryVariant: string;
  primaryContrast: string;
} {
  // the objective of this technique is specifically to make sure that users can read text that is presented over a community color background.
  const contrastRatio = getContrastRatio(
    getRgbColorFromHex(LIGHTTEXTCOLOR),
    getRgbColorFromHex(backgroundColor)
  );

  // light community primary color scenario
  if (contrastRatio > 1 / THRESHOLD) {
    return {
      textColor: DARKTEXTCOLOR,
      primaryVariant: adjustColor(backgroundColor, -PRIMARYVARIANTCOLORADJUSTMENT),
      primaryContrast: adjustColor(backgroundColor, -PRIMARYCONTRASTCOLORADJUSTMENT),
    };
  }
  // dark community primary color scenario
  else {
    return {
      textColor: LIGHTTEXTCOLOR,
      primaryVariant: adjustColor(backgroundColor, PRIMARYVARIANTCOLORADJUSTMENT),
      primaryContrast: adjustColor(backgroundColor, PRIMARYCONTRASTCOLORADJUSTMENT),
    };
  }
}

// The methods below follow the procedure defined officially by the World Wide Web Consortium (W3C) at: https://www.w3.org/TR/WCAG20-TECHS/G18.html#G18-tests

type RGB = [number, number, number];

/**
 * Takes two parameters (colors in RGB format) and returns the contrast ratio between the two colors specified
 * @param foregroundColor
 * @param backgroundColor
 * @returns contrast ratio between the two colors specified
 */
function getContrastRatio(foregroundColor: RGB, backgroundColor: RGB): number {
  const foregroundLuminance = getLuminance(foregroundColor);
  const backgroundLuminance = getLuminance(backgroundColor);
  return backgroundLuminance < foregroundLuminance
    ? (backgroundLuminance + 0.05) / (foregroundLuminance + 0.05)
    : (foregroundLuminance + 0.05) / (backgroundLuminance + 0.05);
}

// function to determine the perceived brightness of an RGB color
function getLuminance(rgb: RGB) {
  const [r, g, b] = rgb.map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return r * 0.2126 + g * 0.7152 + b * 0.0722;
}

// Expand shorthand hex color code (e.g., 'abc' to 'aabbcc')
export function expandShorthandHex(hex: string): string {
  return hex.length === 3 ? hex.replace(/./g, "$&$&") : hex;
}

function getRgbColorFromHex(hex: string): RGB {
  hex = hex.slice(1);

  hex = expandShorthandHex(hex);

  const value = parseInt(hex, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;

  return [r, g, b] as RGB;
}

/**
 * Takes two parameters and returns an adjusted color variant
 * @param color that needs to be adjusted
 * @param amount positive will make the color lighter, negative will make the color darker
 * @returns adjusted color variant in hex form
 */
function adjustColor(color: string, amount: number): string {
  return (
    "#" +
    color.replace(/^#/, "").replace(/../g, (color) =>
      (
        "0" +
        Math.min(255, Math.max(0, parseInt(color, 16) + amount))
          .toString(16)
          .padStart(2, "0")
      ).substring(1)
    )
  );
}

// Convert hex to modern rgb in order to be able to use opacity on tailwind custom colors, see more at: https://tailwindcss.com/docs/customizing-colors#using-css-variables
export function convertHexToModernRgb(hex: string) {
  const hexColor = hex.replace("#", "");
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  return `${r} ${g} ${b}`;
}

export function convertModernRgbToHex(rgb: string) {
  const [r, g, b] = rgb.split(" ").map((v) => parseInt(v));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b
    .toString(16)
    .padStart(2, "0")}`;
}
